import { Box, Card, Option } from "@mui/joy";
import DarkSelect from "../../common/DarkSelect";
import type { LeaveRequestSortBy, LeaveType } from "../../../api/leave/Leave.typed";
import type { UserWithRole } from "../../../api/auth/Auth.typed";
import { hrCardSx, hrFilterSelectSx, textTokens } from "../../../styles/globalStyles";
import { userDisplayName } from "../../../utils/userHelper";

export type StatusOption = { id: number; name: string };

export type LeaveValidationFiltersProps = {
  employees: UserWithRole[];
  leaveTypes: LeaveType[];
  statusOptions: StatusOption[];
  selectedUserId: number;
  statusFilter: number;
  typeFilter: number;
  sortBy: LeaveRequestSortBy;
  yearFilter: number;
  monthFilter: number;
  onEmployeeChange: (userId: number) => void;
  onStatusChange: (statusId: number) => void;
  onTypeChange: (typeId: number) => void;
  onSortByChange: (sort: LeaveRequestSortBy) => void;
  onYearChange: (year: number) => void;
  onMonthChange: (month: number) => void;
};

const SORT_OPTIONS: { value: LeaveRequestSortBy; label: string }[] = [
  { value: "start_date_desc", label: "Début ↓" },
  { value: "start_date_asc", label: "Début ↑" },
  { value: "created_at_desc", label: "Demandé ↓" },
  { value: "created_at_asc", label: "Demandé ↑" },
];

const MONTH_OPTIONS = [
  { value: -1, label: "Tous les mois" },
  { value: 1, label: "Janvier" },
  { value: 2, label: "Février" },
  { value: 3, label: "Mars" },
  { value: 4, label: "Avril" },
  { value: 5, label: "Mai" },
  { value: 6, label: "Juin" },
  { value: 7, label: "Juillet" },
  { value: 8, label: "Août" },
  { value: 9, label: "Septembre" },
  { value: 10, label: "Octobre" },
  { value: 11, label: "Novembre" },
  { value: 12, label: "Décembre" },
];

function buildYearOptions(): number[] {
  const current = new Date().getFullYear();
  const years: number[] = [];

  for (let y = current + 1; y >= current - 4; y--) {
    years.push(y);
  }

  return years;
}

const YEAR_OPTIONS = buildYearOptions();

const filterSelectSx = {
  ...hrFilterSelectSx,
  color: textTokens.color.primary,
  minWidth: { xs: 0, sm: 130 },
  flex: { xs: "1 1 100%", sm: "1 1 130px" },
  width: { xs: "100%", sm: "auto" },
  maxWidth: { xs: "100%", sm: "none" },
  fontSize: "0.8rem",
};

const periodFiltersRowSx = {
  display: "flex",
  flexWrap: "nowrap",
  gap: { xs: 0.5, sm: 1 },
  alignItems: "center",
  minWidth: 0,
  width: "100%",
};

const yearPeriodSelectSx = {
  ...filterSelectSx,
  minWidth: { xs: 88, sm: 100 },
  flex: { xs: "0 0 96px", sm: "0 0 100px" },
  width: "auto",
};

const monthPeriodSelectSx = {
  ...filterSelectSx,
  minWidth: 0,
  flex: { xs: "1 1 0", sm: "1 1 140px" },
  width: "auto",
  maxWidth: "100%",
};

const sortPeriodSelectSx = {
  ...filterSelectSx,
  minWidth: { xs: 88, sm: 110 },
  flex: { xs: "0 1 100px", sm: "0 0 110px" },
  width: "auto",
  maxWidth: { xs: 110, sm: "none" },
};

export default function LeaveValidationFilters({
  employees,
  leaveTypes,
  statusOptions,
  selectedUserId,
  statusFilter,
  typeFilter,
  sortBy,
  yearFilter,
  monthFilter,
  onEmployeeChange,
  onStatusChange,
  onTypeChange,
  onSortByChange,
  onYearChange,
  onMonthChange,
}: LeaveValidationFiltersProps) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
      {/* Filtres principaux */}
      <Card sx={{ p: 1.25, ...hrCardSx }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
          <DarkSelect
            placeholder="Salarié"
            value={selectedUserId}
            onChange={(_, value) =>
              onEmployeeChange(value === null || value === undefined ? -1 : Number(value))
            }
            sx={filterSelectSx}
          >
            <Option value={-1}>Tous les salariés</Option>
            {employees.map((u) => (
              <Option key={u.user.id} value={u.user.id}>
                {userDisplayName(u.user)}
              </Option>
            ))}
          </DarkSelect>

          <DarkSelect
            placeholder="Statut"
            value={statusFilter}
            onChange={(_, value) =>
              onStatusChange(value === null || value === undefined ? -1 : Number(value))
            }
            sx={filterSelectSx}
          >
            {statusOptions.map((s) => (
              <Option key={s.id} value={s.id}>
                {s.name}
              </Option>
            ))}
          </DarkSelect>

          <DarkSelect
            placeholder="Type"
            value={typeFilter}
            onChange={(_, value) =>
              onTypeChange(value === null || value === undefined ? -1 : Number(value))
            }
            sx={filterSelectSx}
          >
            <Option value={-1}>Tous les types</Option>
            {leaveTypes.map((t) => (
              <Option key={t.ID} value={t.ID}>
                {t.Name}
              </Option>
            ))}
          </DarkSelect>
        </Box>
      </Card>

      {/* Filtres période */}
      <Card sx={{ p: 1.25, ...hrCardSx }}>
        <Box sx={periodFiltersRowSx}>
          <DarkSelect
            value={yearFilter}
            onChange={(_, value) =>
              onYearChange(value === null ? new Date().getFullYear() : Number(value))
            }
            sx={yearPeriodSelectSx}
          >
            {YEAR_OPTIONS.map((y) => (
              <Option key={y} value={y}>
                {y}
              </Option>
            ))}
          </DarkSelect>

          <DarkSelect
            value={monthFilter}
            onChange={(_, value) => onMonthChange(value === null ? -1 : Number(value))}
            sx={monthPeriodSelectSx}
          >
            {MONTH_OPTIONS.map((m) => (
              <Option key={m.value} value={m.value}>
                {m.label}
              </Option>
            ))}
          </DarkSelect>

          <DarkSelect
            value={sortBy}
            onChange={(_, value) =>
              onSortByChange((value as LeaveRequestSortBy) ?? "start_date_desc")
            }
            sx={sortPeriodSelectSx}
          >
            {SORT_OPTIONS.map((opt) => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </DarkSelect>
        </Box>
      </Card>
    </Box>
  );
}
